import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { AlertCircle, Check, ChevronDown, ChevronRight, ListPlus, Loader2, PanelLeftClose, PanelLeftOpen, Plus, RotateCcw, Search, X, PackageSearch } from "lucide-react";
import type { CostLibraryTask } from "@shared/schema";
import { getNavSectionDisplayLabel, getNavItemDisplayLabel } from "@workspace/api-zod/quote-headings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/hooks/use-auth";
import { useQuoteHeadingPreferences } from "@/hooks/use-quote-heading-preferences";
import { useCostTaskFavourites } from "@/hooks/use-cost-task-favourites";
import { useOrderedSections } from "@/components/category-sidebar";
import { getCostTaskBrowseMemberships } from "@/lib/cost-library-membership";
import { buildCostLibraryNavigation } from "@/lib/cost-library-navigation";
import { useCostLibrary, getCostLibraryQueryKey } from "@/hooks/use-cost-library";
import { catalogueTaskKey } from "@/lib/quote-catalogue-item";
import { cn, formatMoney } from "@/lib/utils";

/** Width of the docked browser and the minimum editor width before docking. */
export const ADD_ITEMS_BROWSER_WIDTH = 272;
export const ADD_ITEMS_MIN_EDITOR_WIDTH = 600;
const DOCK_GAP = 16;
const DOCK_THRESHOLD = ADD_ITEMS_BROWSER_WIDTH + ADD_ITEMS_MIN_EDITOR_WIDTH + DOCK_GAP;
const UNMAPPED_ID = "__unmapped";
const SEARCH_PAGE_SIZE = 60;
const ITEM_ACTION_CLASS = "mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-primary/40 bg-transparent text-primary transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

function FavouriteStar({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
    </svg>
  );
}

export function getCostLibraryBrowserQueryKey(accountId: string | number | null, market: string | null | undefined) {
  return getCostLibraryQueryKey(accountId, market);
}

/** Visible, active tasks only. Exported for tests. */
export function filterBrowsableTasks(tasks: readonly CostLibraryTask[]): CostLibraryTask[] {
  return tasks.filter((t) => !t.isHidden && t.isActive !== false);
}

export function searchCatalogueTasks(tasks: readonly CostLibraryTask[], query: string): CostLibraryTask[] {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return [];
  const scored: Array<{ t: CostLibraryTask; s: number }> = [];
  for (const t of tasks) {
    const name = t.name.toLowerCase();
    const rest = `${t.description ?? ""} ${t.keywords ?? ""}`.toLowerCase();
    let s = 0;
    let ok = true;
    for (const term of terms) {
      if (name.includes(term)) s += name.startsWith(term) ? 3 : 2;
      else if (rest.includes(term)) s += 1;
      else { ok = false; break; }
    }
    if (ok) scored.push({ t, s });
  }
  return scored.sort((a, b) => b.s - a.s || a.t.name.localeCompare(b.t.name)).map((x) => x.t);
}

type AddState = { pendingKey: string | null; errors: Record<string, string>; confirmKey: string | null; justAdded: string | null };

function useAddController(onAdd: (task: CostLibraryTask) => Promise<void>) {
  const [state, setState] = useState<AddState>({ pendingKey: null, errors: {}, confirmKey: null, justAdded: null });
  const onAddRef = useRef(onAdd);
  onAddRef.current = onAdd;
  const busy = useRef(false);
  const add = useCallback(async (task: CostLibraryTask) => {
    if (busy.current) return;
    busy.current = true;
    const key = catalogueTaskKey(task);
    setState((s) => { const errors = { ...s.errors }; delete errors[key]; return { ...s, pendingKey: key, errors, confirmKey: null }; });
    try {
      await onAddRef.current(task);
      setState((s) => ({ ...s, pendingKey: null, justAdded: key }));
    } catch (e) {
      const msg = e instanceof Error && e.message ? e.message : "Could not add this item.";
      setState((s) => ({ ...s, pendingKey: null, errors: { ...s.errors, [key]: msg } }));
    } finally {
      busy.current = false;
    }
  }, []);
  const askConfirm = useCallback((key: string | null) => setState((s) => ({ ...s, confirmKey: key })), []);
  return { ...state, add, askConfirm };
}
type AddController = ReturnType<typeof useAddController>;

type FavouritesController = ReturnType<typeof useCostTaskFavourites>;

function TaskRow({ task, market, added, ctl, context, favourites }: { task: CostLibraryTask; market?: string | null; added: boolean; ctl: AddController; context?: string; favourites: FavouritesController }) {
  const key = catalogueTaskKey(task);
  const pending = ctl.pendingKey === key;
  const disabled = ctl.pendingKey !== null;
  const error = ctl.errors[key];
  const confirming = ctl.confirmKey === key;
  const tid = key.replace(/[^a-zA-Z0-9_-]/g, "-");
  return (
    <li className="group rounded-md border border-transparent px-2 py-1.5 hover:border-border hover:bg-card transition-colors" data-testid={`row-add-item-${tid}`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[13px] leading-snug text-foreground break-words [overflow-wrap:anywhere]" data-testid={`text-add-item-name-${tid}`}>{task.name}</p>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[11px] text-muted-foreground">
            <span className="font-mono tabular-nums text-foreground/80" data-testid={`text-add-item-rate-${tid}`}>{formatMoney(task.rateTypical, market)}</span>
            <span>/ {task.unit}</span>
            {task.source !== "default" && <span className="rounded bg-primary/15 px-1 text-[10px] uppercase tracking-wide text-primary">{task.source === "custom" ? "Custom" : "Your rate"}</span>}
          </p>
          {context && <p className="mt-0.5 truncate text-[10px] uppercase tracking-wider text-muted-foreground/80">{context}</p>}
          {added && !confirming && (
            <p className="mt-1 flex items-center gap-1 text-[11px] text-primary" data-testid={`status-already-added-${tid}`}>
              <Check className="h-3 w-3" aria-hidden="true" /> Already added
              <button type="button" disabled={disabled} onClick={() => ctl.askConfirm(key)} className="ml-1 underline underline-offset-2 hover:text-foreground disabled:opacity-50" data-testid={`button-add-another-${tid}`}>Add another</button>
            </p>
          )}
          {confirming && (
            <div className="mt-1.5 flex items-center gap-1.5 text-[11px]" role="group" aria-label={`Confirm adding another ${task.name}`}>
              <span className="text-muted-foreground">Add a second line?</span>
              <Button type="button" size="sm" className="h-6 px-2 text-[11px]" disabled={disabled} onClick={() => ctl.add(task)} data-testid={`button-confirm-add-another-${tid}`}>Add</Button>
              <Button type="button" size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={() => ctl.askConfirm(null)} data-testid={`button-cancel-add-another-${tid}`}>Cancel</Button>
            </div>
          )}
          {error && (
            <p className="mt-1 flex items-start gap-1 text-[11px] text-destructive" role="alert" data-testid={`error-add-item-${tid}`}>
              <AlertCircle className="mt-px h-3 w-3 shrink-0" aria-hidden="true" />
              <span className="min-w-0 flex-1">{error}</span>
              <button type="button" disabled={disabled} onClick={() => ctl.add(task)} className="inline-flex items-center gap-0.5 font-medium underline underline-offset-2 disabled:opacity-50" data-testid={`button-retry-add-${tid}`}>
                <RotateCcw className="h-3 w-3" aria-hidden="true" /> Retry
              </button>
            </p>
          )}
        </div>
        <button type="button"
          aria-label={`${favourites.keys.has(key) ? "Unfavourite" : "Favourite"} ${task.name}`}
          aria-pressed={favourites.keys.has(key)}
          title={favourites.keys.has(key) ? "Remove from favourites" : "Add to favourites"}
          disabled={favourites.unavailable || favourites.pending}
          onClick={() => void favourites.toggle(task)}
          className={cn(ITEM_ACTION_CLASS, favourites.keys.has(key) && "border-primary ring-1 ring-primary/30")}
          data-testid={`button-favourite-${tid}`}>
          <FavouriteStar className="h-3.5 w-3.5" />
        </button>
        {!added && (
          <button
            type="button"
            disabled={disabled}
            onClick={() => ctl.add(task)}
            aria-label={`Add ${task.name} to estimate`}
            title="Add to estimate"
            className={ITEM_ACTION_CLASS}
            data-testid={`button-add-item-${tid}`}
          >
            {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Plus className="h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" />}
          </button>
        )}
      </div>
    </li>
  );
}

function BrowserSkeleton() {
  return (
    <div className="space-y-2 p-3" aria-busy="true" aria-label="Loading cost library" data-testid="status-add-items-loading">
      {Array.from({ length: 9 }).map((_, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="h-4 w-4 rounded bg-muted animate-pulse" />
          <div className="h-3 rounded bg-muted animate-pulse" style={{ width: `${48 + ((i * 37) % 40)}%` }} />
        </div>
      ))}
    </div>
  );
}

type BrowserProps = {
  tasks: CostLibraryTask[] | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  market?: string | null;
  headingNames?: Record<string, string> | null;
  addedTaskKeys: ReadonlySet<string>;
  ctl: AddController;
  search: string;
  setSearch: (v: string) => void;
  expanded: Set<string>;
  toggle: (id: string) => void;
  searchRef?: React.Ref<HTMLInputElement>;
  favourites: FavouritesController;
  favouritesOnly: boolean;
  setFavouritesOnly: (value: boolean) => void;
};

function AddItemsBrowser({ tasks, isLoading, error, refetch, market, headingNames, addedTaskKeys, ctl, search, setSearch, expanded, toggle, searchRef, favourites, favouritesOnly, setFavouritesOnly }: BrowserProps) {
  const [resultLimit, setResultLimit] = useState(SEARCH_PAGE_SIZE);
  useEffect(() => setResultLimit(SEARCH_PAGE_SIZE), [search, favouritesOnly]);
  const navigation = useMemo(() => buildCostLibraryNavigation(tasks ?? [], market), [tasks, market]);
  const { sections } = useOrderedSections(navigation);
  const visible = useMemo(() => filterBrowsableTasks(tasks ?? []), [tasks]);

  const grouped = useMemo(() => {
    const byItem = new Map<string, CostLibraryTask[]>();
    const bySection = new Map<string, CostLibraryTask[]>();
    const locByKey = new Map<string, string>();
    const unmapped: CostLibraryTask[] = [];
    const distinct = new Map(visible.map(task => [catalogueTaskKey(task), task]));
    for (const t of distinct.values()) {
      const memberships = getCostTaskBrowseMemberships(t, sections);
      if (!memberships.length) { unmapped.push(t); continue; }
      locByKey.set(catalogueTaskKey(t), memberships.map(({ section, items }) => {
        const label = getNavSectionDisplayLabel(section, headingNames);
        return items.length === 1 ? `${label} / ${getNavItemDisplayLabel(section.id, items[0], headingNames)}` : label;
      }).join(" · "));
      for (const { section, items } of memberships) {
        const sectionTasks = bySection.get(section.id) ?? [];
        sectionTasks.push(t);
        bySection.set(section.id, sectionTasks);
        for (const item of items) {
          const k = `${section.id}/${item.id}`;
          const arr = byItem.get(k) ?? [];
          arr.push(t);
          byItem.set(k, arr);
        }
      }
    }
    for (const arr of [...byItem.values(), ...bySection.values()]) arr.sort((a, b) => a.name.localeCompare(b.name));
    unmapped.sort((a, b) => a.name.localeCompare(b.name));
    return { byItem, bySection, unmapped, locByKey };
  }, [visible, headingNames, sections]);

  const favouriteTasks = useMemo(() => visible.filter(t => favourites.keys.has(catalogueTaskKey(t)))
    .sort((a, b) => a.name.localeCompare(b.name)), [visible, favourites.keys]);
  const results = useMemo(() => search.trim() ? searchCatalogueTasks(favouritesOnly ? favouriteTasks : visible, search)
    : favouriteTasks, [visible, favouriteTasks, search, favouritesOnly]);
  const searching = search.trim().length > 0;

  const row = (t: CostLibraryTask, context?: string) => (
    <TaskRow key={catalogueTaskKey(t)} task={t} market={market} added={addedTaskKeys.has(catalogueTaskKey(t))} ctl={ctl} context={context} favourites={favourites} />
  );

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="panel-add-items-browser">
      <div className="border-b p-2.5">
        <div className="mb-2 flex gap-1" role="group" aria-label="Work item view">
          <Button type="button" size="sm" variant={favouritesOnly ? "ghost" : "secondary"} className="h-9 flex-1 px-2 text-xs" aria-pressed={!favouritesOnly} onClick={() => setFavouritesOnly(false)}>All items</Button>
          <Button type="button" size="sm" variant={favouritesOnly ? "secondary" : "ghost"} className="h-9 flex-1 px-2 text-xs" aria-pressed={favouritesOnly} onClick={() => setFavouritesOnly(true)}>
            <FavouriteStar className="mr-1 h-3.5 w-3.5 text-primary" /> Favourites
          </Button>
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            ref={searchRef}
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={favouritesOnly ? "Search favourites" : "Search all work items"}
            aria-label={favouritesOnly ? "Search favourite work items" : "Search work items across all categories"}
            className="h-8 pl-8 pr-7 text-sm"
            data-testid="input-add-items-search"
          />
          {searching && (
            <button type="button" onClick={() => setSearch("")} aria-label="Clear search" className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground" data-testid="button-clear-add-items-search">
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          )}
        </div>
        {favourites.error && <div role="alert" className="mt-2 text-xs text-destructive">
          {favourites.error} <button type="button" className="underline" onClick={favourites.retry}>Retry favourites</button>
        </div>}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {isLoading ? (
          <BrowserSkeleton />
        ) : error ? (
          <div className="flex flex-col items-center gap-2 px-4 py-8 text-center" role="alert" data-testid="status-add-items-error">
            <AlertCircle className="h-6 w-6 text-destructive" aria-hidden="true" />
            <p className="text-sm font-medium">Library didn't load</p>
            <p className="text-xs text-muted-foreground">{error.message}</p>
            <Button type="button" size="sm" variant="outline" className="mt-1 h-7" onClick={refetch} data-testid="button-retry-add-items">
              <RotateCcw className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> Try again
            </Button>
          </div>
        ) : visible.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-4 py-10 text-center" data-testid="status-add-items-empty">
            <PackageSearch className="h-7 w-7 text-primary/70" aria-hidden="true" />
            <p className="text-sm font-medium">No library items available</p>
            <p className="text-xs text-muted-foreground">Add or restore tasks in the Cost Library and they'll show up here.</p>
          </div>
        ) : favouritesOnly && favourites.loading ? (
          <p className="p-4 text-xs text-muted-foreground" role="status">Loading favourites…</p>
        ) : favouritesOnly && favourites.unavailable ? (
          <p className="p-4 text-xs text-muted-foreground">Favourites are unavailable. You can still browse All items.</p>
        ) : favouritesOnly && favouriteTasks.length === 0 ? (
          <div className="p-4 text-xs text-muted-foreground" data-testid="status-favourites-empty">
            No available favourites yet. Star work items in All items to keep them here. Hidden, inactive or other-market tasks won't appear.
          </div>
        ) : searching || favouritesOnly ? (
          <div className="p-1.5">
            <p className="px-2 py-1 text-[11px] text-muted-foreground" aria-live="polite" data-testid="text-add-items-result-count">
              {results.length === 0 ? "No work items match" : `${results.length} match${results.length === 1 ? "" : "es"}${results.length > resultLimit ? `, showing first ${resultLimit}` : ""}`}
            </p>
            {results.length === 0 ? (
              <p className="px-2 py-4 text-xs text-muted-foreground">Try a shorter term, a material, or a trade, for example "skim" or "lintel".</p>
            ) : (
              <>
                <ul className="space-y-0.5">{results.slice(0, resultLimit).map((t) => row(t, grouped.locByKey.get(catalogueTaskKey(t)) ?? "Uncategorised library items"))}</ul>
                {results.length > resultLimit && <Button type="button" size="sm" variant="ghost"
                  className="w-full text-xs" onClick={() => setResultLimit(n => n + SEARCH_PAGE_SIZE)}>
                  Show more work items
                </Button>}
              </>
            )}
          </div>
        ) : (
          <nav aria-label="Cost library categories" className="py-1">
            {sections.map((section) => {
              const items = section.items.filter((i) => grouped.byItem.has(`${section.id}/${i.id}`));
              const sectionTasks = grouped.bySection.get(section.id) ?? [];
              if (!sectionTasks.length) return null;
              const SectionIcon = section.icon;
              const open = expanded.has(section.id);
              const label = getNavSectionDisplayLabel(section, headingNames);
              const allId = `${section.id}/__all`;
              return (
                <div key={section.id}>
                  <button type="button" aria-expanded={open} onClick={() => toggle(section.id)} className="flex w-full items-center gap-2 px-2.5 py-2 text-left hover-elevate" data-testid={`button-add-items-section-${section.id}`}>
                    <SectionIcon className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                    <span className="min-w-0 flex-1 text-[11px] font-semibold uppercase leading-snug tracking-wider text-primary break-words">{label}</span>
                    {open ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />}
                  </button>
                  {open && (
                    <div>
                      <button type="button" aria-expanded={expanded.has(allId)} onClick={() => toggle(allId)} className="flex w-full items-center gap-2 py-1.5 pl-6 pr-2.5 text-left text-[13px] text-muted-foreground hover-elevate" data-testid={`button-add-items-all-${section.id}`}>
                        <span className="min-w-0 flex-1">All {label}</span>
                      </button>
                      {expanded.has(allId) && <ul className="ml-7 mr-1 space-y-0.5 border-l border-primary/25 pb-1 pl-1">{sectionTasks.map(t => row(t))}</ul>}
                    </div>
                  )}
                  {open && items.map((item) => {
                    const ItemIcon = item.icon;
                    const id = `${section.id}/${item.id}`;
                    const itemOpen = expanded.has(id);
                    const list = grouped.byItem.get(id) ?? [];
                    return (
                      <div key={item.id}>
                        <button type="button" aria-expanded={itemOpen} onClick={() => toggle(id)} className={cn("flex w-full items-center gap-2 py-1.5 pl-6 pr-2.5 text-left hover-elevate", itemOpen ? "text-foreground" : "text-muted-foreground")} data-testid={`button-add-items-sub-${item.id}`}>
                          <ItemIcon className={cn("h-3.5 w-3.5 shrink-0", itemOpen ? "text-primary" : "")} aria-hidden="true" />
                          <span className="min-w-0 flex-1 text-[13px] leading-snug break-words">{getNavItemDisplayLabel(section.id, item, headingNames)}</span>
                        </button>
                        {itemOpen && <ul className="ml-7 mr-1 space-y-0.5 border-l border-primary/25 pb-1 pl-1">{list.map((t) => row(t))}</ul>}
                      </div>
                    );
                  })}
                </div>
              );
            })}
            {grouped.unmapped.length > 0 && (
              <div>
                <button type="button" aria-expanded={expanded.has(UNMAPPED_ID)} onClick={() => toggle(UNMAPPED_ID)} className="flex w-full items-center gap-2 px-2.5 py-2 text-left hover-elevate" data-testid="button-add-items-section-unmapped">
                  <ListPlus className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                  <span className="min-w-0 flex-1 text-[11px] font-semibold uppercase tracking-wider text-primary">Uncategorised library items</span>
                  {expanded.has(UNMAPPED_ID) ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />}
                </button>
                {expanded.has(UNMAPPED_ID) && <>
                  <p className="px-3 pb-1 text-[11px] text-muted-foreground">These items do not match the library navigation filters. They are not in the Other category.</p>
                  <ul className="ml-4 mr-1 space-y-0.5 border-l border-primary/25 pb-1 pl-1">{grouped.unmapped.map((t) => row(t, `${t.category} / ${t.trade}`))}</ul>
                </>}
              </div>
            )}
          </nav>
        )}
      </div>
      <p className="border-t px-3 py-1.5 text-[10px] leading-snug text-muted-foreground">New lines start at quantity 1. Set the real quantity in the estimate.</p>
    </div>
  );
}

function useWidth<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([e]) => setWidth(e!.contentRect.width));
    ro.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);
  return { ref, width };
}

export function QuoteAddItemsLayout({ children, onAdd, addedTaskKeys, enabled = true, toolbar }: {
  children: ReactNode;
  toolbar?: ReactNode;
  onAdd: (task: CostLibraryTask) => Promise<void>;
  addedTaskKeys: ReadonlySet<string>;
  enabled?: boolean;
}) {
  const { user } = useAuth();
  const accountId = (user as { id?: string | number } | null | undefined)?.id ?? null;
  const active = enabled && accountId != null;
  if (!active) return <>{toolbar}{children}</>;
  return <ActiveLayout key={`${accountId}:${user?.market}`} onAdd={onAdd} addedTaskKeys={addedTaskKeys} accountId={accountId} market={user?.market} toolbar={toolbar}>{children}</ActiveLayout>;
}

function ActiveLayout({ children, onAdd, addedTaskKeys, accountId, market, toolbar }: {
  toolbar?: ReactNode;
  children: ReactNode; onAdd: (task: CostLibraryTask) => Promise<void>; addedTaskKeys: ReadonlySet<string>; accountId: string | number; market?: string | null;
}) {
  const query = useCostLibrary();
  const { names: headingNames } = useQuoteHeadingPreferences(true);
  const favourites = useCostTaskFavourites(accountId);
  const [favouritesOnly, setFavouritesOnly] = useState(false);
  const ctl = useAddController(async task => {
    try { await onAdd(task); }
    catch (error) {
      // An add can reject a stale library rate/unit; refresh before retrying it.
      void query.refetch();
      throw error;
    }
  });
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const toggle = useCallback((id: string) => setExpanded((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; }), []);
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { ref, width } = useWidth<HTMLDivElement>();
  const docked = width >= DOCK_THRESHOLD;
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const showSidebarRef = useRef<HTMLButtonElement>(null);
  const hideSidebarRef = useRef<HTMLButtonElement>(null);
  const wasHidden = useRef(false);

  useEffect(() => { if (docked) setOpen(false); }, [docked]);
  useEffect(() => {
    if (!docked) return;
    if (!sidebarVisible) showSidebarRef.current?.focus();
    else if (wasHidden.current) hideSidebarRef.current?.focus();
    wasHidden.current = !sidebarVisible;
  }, [docked, sidebarVisible]);

  const browserProps = {
    tasks: query.data, isLoading: query.isLoading, error: query.error ?? null, refetch: () => { void query.refetch(); },
    market, headingNames, addedTaskKeys, ctl, search, setSearch, expanded, toggle, favourites, favouritesOnly, setFavouritesOnly,
  };

  return (
    <div ref={ref} className="w-full min-w-0" data-testid="layout-quote-add-items">
      {docked ? (
        <>
        {toolbar && <div className="mb-3">{toolbar}</div>}
        {!sidebarVisible && (
          <div className="mb-3 flex justify-start">
            <Button type="button" ref={showSidebarRef} variant="outline" size="sm" onClick={() => setSidebarVisible(true)}
              aria-expanded={false} className="h-8 gap-1.5 border-primary/40 text-primary" data-testid="button-show-add-items">
              <PanelLeftOpen className="h-4 w-4" aria-hidden="true" /> Show add items
            </Button>
          </div>
        )}
        <div className="flex items-stretch" style={{ gap: DOCK_GAP }} data-testid="estimate-items-columns">
          {sidebarVisible && <aside
            aria-label="Add items from cost library"
            className="relative min-h-80 shrink-0 rounded-lg border bg-card/60"
            style={{ width: ADD_ITEMS_BROWSER_WIDTH }}
            data-testid="sidebar-add-items"
          >
            <div className="absolute inset-0">
              <div className="sticky top-2 flex h-full max-h-[calc(100dvh-1rem)] min-h-0 flex-col overflow-hidden rounded-lg">
                <div className="flex h-11 shrink-0 items-center gap-2 border-b bg-sidebar px-3 text-sidebar-foreground" data-testid="header-add-items">
                  <ListPlus className="h-4 w-4 text-sidebar-primary" aria-hidden="true" />
                  <h2 className="text-xs font-semibold uppercase tracking-wider">Add items</h2>
                  <button type="button" ref={hideSidebarRef} onClick={() => setSidebarVisible(false)}
                    aria-label="Hide add items" aria-expanded={true} title="Hide add items"
                    className="ml-auto inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-sidebar-foreground hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-primary"
                    data-testid="button-hide-add-items">
                    <PanelLeftClose className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
                <div className="min-h-0 flex-1"><AddItemsBrowser {...browserProps} /></div>
              </div>
            </div>
          </aside>}
          <div className="min-w-0 flex-1" data-testid="estimate-items-column">{children}</div>
        </div>
        </>
      ) : (
        <>
          <div className="mb-3 flex items-end justify-end gap-2 sm:gap-3">
            {toolbar && <div className="min-w-0 flex-1">{toolbar}</div>}
            <Button type="button" ref={triggerRef} variant="outline" size="sm" onClick={() => setOpen(true)} aria-haspopup="dialog" aria-expanded={open} className="h-8 shrink-0 gap-1.5 border-primary/40 px-2 text-xs text-primary sm:px-3 sm:text-sm" data-testid="button-open-add-items">
              <ListPlus className="h-4 w-4" aria-hidden="true" /> Add items
            </Button>
          </div>
          {children}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetContent
              side="left"
              className="flex w-[min(22rem,92vw)] flex-col gap-0 p-0 sm:max-w-sm"
              onCloseAutoFocus={(e) => { e.preventDefault(); triggerRef.current?.focus(); }}
              data-testid="drawer-add-items"
            >
              <SheetHeader className="border-b bg-sidebar px-4 py-3 text-left text-sidebar-foreground">
                <SheetTitle className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-sidebar-foreground">
                  <ListPlus className="h-4 w-4 text-sidebar-primary" aria-hidden="true" /> Add items
                </SheetTitle>
                <SheetDescription className="text-xs text-sidebar-foreground/75">Browse or search your cost library. Your estimate stays as it is until you add a line.</SheetDescription>
              </SheetHeader>
              <div className="min-h-0 flex-1"><AddItemsBrowser {...browserProps} /></div>
            </SheetContent>
          </Sheet>
        </>
      )}
    </div>
  );
}
